import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import Icon from 'assets/components/icon/Icon';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import { getLocales, Language } from 'assets/locales/Locale';
import { LocationSearch } from 'assets/templates/locationSearch/LocationSearch';
import Theme from 'assets/themes/Theme.Common';
import { formatFormPhone } from 'assets/utils/forms/formatFormPhone';
import { isValidPhoneNumber } from 'assets/utils/parsersAndValidation/Validators';
import { HttpStatus } from 'config/Api.Config';
import { set } from 'lodash';
import contractTransferToNewClientApi from 'models/sales/contract/transferToNewClient/Api.Contract.TransferToNewClient';
import { SalesContractAction, useSalesContractProvider } from '../../Sales.Contract.Hooks';
import contractFindApi from 'models/sales/contract/find/Api.Contract.Find';
import { inputStyles } from 'assets/components/inputs/Input';
import CheckInput from 'assets/components/inputs/check/CheckInput';

const ContractTransferToNewClient = () => {
  const { lang } = getLocales();
  const { selected, findApi, listApi, pageRouter, regenerateContractDocuments } = useSalesContractProvider();

  return (
    <ModalForm<Api.Core.Contract.TransferToNewClient.IRequest>
      subcomponents={ThemeModalFormDefault}
      dependencyArray={[selected]}
      title={lang.transferToNewClient}
      default={{
        isTransferInvisible: false,
        client: {},
      }}
      onCancel={() => pageRouter.updateParams({ id: selected?.id?.toString(), action: SalesContractAction.INFO })}
      onSubmit={async (data) => {
        const res = await contractTransferToNewClientApi({ contractId: selected?.id }, data);
        if (res.status !== HttpStatus.ok) {
          addLog({ error: res.message });
        } else {
          const refetchedContract = (await contractFindApi({ id: selected?.id }))?.payload;
          await regenerateContractDocuments(
            refetchedContract,
            refetchedContract?.documentLanguageIsoCode ??
              refetchedContract?.dealer?.documentLanguageIsoCode ??
              Language.english
          );
          addLog({ success: lang.saveSuccess });
          pageRouter.updateParams({ id: selected.id.toString(), action: SalesContractAction.INFO });
          listApi.execute((body) => body);
          findApi.execute((body) => body);
        }
      }}
      validation={(data, errors) => {
        if (!data?.client?.phone) set(errors, 'phone', lang.mustNotBeEmpty);
        if (data?.client?.phone && !isValidPhoneNumber(data?.client?.phone))
          set(errors, 'phone', lang.invalidFormatRequires.replace('{format}', '###-###-####'));
        if (!data?.client?.firstName) set(errors, 'client.firstName', lang.mustNotBeEmpty);
        if (!data?.client?.lastName) set(errors, 'client.lastName', lang.mustNotBeEmpty);
        if (!data?.client?.email) set(errors, 'client.email', lang.mustNotBeEmpty);
        if (!data?.client?.streetAddress) set(errors, 'client.streetAddress', lang.mustNotBeEmpty);
        if (!data?.client?.city) set(errors, 'client.city', lang.mustNotBeEmpty);
        if (!data?.client?.provinceCode) set(errors, 'client.provinceCode', lang.mustNotBeEmpty);
        if (!data?.client?.postalCode) set(errors, 'client.postalCode', lang.mustNotBeEmpty);
        if (!data?.client?.countryCode) set(errors, 'client.countryCode', lang.mustNotBeEmpty);
        if (data?.client?.taxExempt && !data?.client?.taxExemptionBand)
          set(errors, 'client.taxExemptionBand', lang.mustNotBeEmpty);
      }}
      render={({ form }) => {
        function updateClient(client: Partial<Model.IClient>) {
          if (client.taxExempt === false) client.taxExemptionBand = null;
          form.merge({ client });
        }
        return (
          <FormWrapper>
            <InputWrapper>
              <TextInput
                name="firstName"
                label={lang.firstName}
                value={form.data?.client?.firstName}
                error={form.errors?.client?.firstName}
                onChange={updateClient}
                icon={<Icon class="if-quill-pen" />}
              />
              <TextInput
                name="lastName"
                label={lang.lastName}
                value={form.data?.client?.lastName}
                error={form.errors?.client?.lastName}
                onChange={updateClient}
                icon={<Icon class="if-quill-pen" />}
              />
            </InputWrapper>
            <InputWrapper>
              <TextInput
                name="email"
                label={lang.email}
                value={form.data?.client?.email}
                error={form.errors?.client?.email}
                onChange={updateClient}
                icon={<Icon class="if-email" />}
              />
              <TextInput
                name="phone"
                label={lang.phoneNumber}
                value={form.data?.client?.phone}
                error={form.errors?.client?.phone}
                onChange={({ phone }) => updateClient({ phone: formatFormPhone(form.data?.client?.phone, phone) })}
                icon={<Icon class="if-phone" />}
              />
            </InputWrapper>
            <LocationSearch
              onSelect={(location) => {
                updateClient({
                  streetAddress: location?.Line1 || null,
                  provinceCode: location?.ProvinceCode || null,
                  city: location?.City || null,
                  countryCode: location?.CountryIso3 || null,
                  postalCode: location?.PostalCode || null,
                });
              }}
            />
            <InputWrapper>
              <TextInput
                name="streetAddress"
                label={lang.streetAddress}
                value={form.data?.client?.streetAddress}
                error={form.errors?.client?.streetAddress}
                onChange={updateClient}
                icon={<Icon class="if-map-pin" />}
              />
              <TextInput
                name="postalCode"
                label={lang.postalCode}
                value={form.data?.client?.postalCode}
                error={form.errors?.client?.postalCode}
                onChange={updateClient}
                icon={<Icon class="if-map-pin" />}
              />
              <TextInput
                name="city"
                label={lang.city}
                value={form.data?.client?.city}
                error={form.errors?.client?.city}
                onChange={updateClient}
                icon={<Icon class="if-map-pin" />}
              />
              <TextInput
                name="provinceCode"
                label={lang.provinceCode}
                value={form.data?.client?.provinceCode}
                error={form.errors?.client?.provinceCode}
                onChange={updateClient}
                uppercase
                icon={<Icon class="if-map-pin" />}
                style={{ width: Theme.Size.XXL }}
              />
              <TextInput
                name="countryCode"
                label={lang.countryCode}
                value={form.data?.client?.countryCode}
                error={form.errors?.client?.countryCode}
                onChange={updateClient}
                icon={<Icon class="if-map-pin" />}
                style={{ width: Theme.Size.XXL }}
              />
            </InputWrapper>
            {form.data?.client?.taxExempt && (
              <TextInput
                name="taxExemptionBand"
                label={lang.taxExemptionBand}
                value={form.data?.client?.taxExemptionBand}
                error={form.errors?.client?.taxExemptionBand}
                onChange={updateClient}
              />
            )}
            <CheckInput
              name="isTransferInvisible"
              class={inputStyles.check.switch}
              label={lang.setIsTransferIndicatorInvisible}
              value={form.data?.isTransferInvisible}
              error={form.errors?.isTransferInvisible}
              onChange={form.update}
              htmlElementProps={{ title: form.data?.isTransferInvisible ? lang.disable : lang.enable }}
            />
          </FormWrapper>
        );
      }}
    />
  );
};

export default ContractTransferToNewClient;
